import { useCallback, useEffect, useRef, useState } from 'react';

export type Async<T> = { data: T | null; error: unknown; loading: boolean; reload: () => void; set: (v: T) => void };

/** Loads data when `deps` change; keeps the previous data while reloading so screens do not flash. */
export function useAsync<T>(load: () => Promise<T>, deps: readonly unknown[]): Async<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const loader = useRef(load);
  loader.current = load;
  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    loader.current().then(
      (v) => { if (live) setData(v); },
      (e: unknown) => { if (live) setError(e); },
    ).finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);
  const reload = useCallback(() => setTick((n) => n + 1), []);
  return { data, error, loading, reload, set: setData };
}
